// Shared Table of Contents scroll-spy. The current entry is the last one that
// has "crossed": a visible heading whose top is at or above the spy line, or —
// when that heading is not in the DOM — a TOC item whose page is at or before
// the page the reader is on. Unbound rows never win.
//
// Until the first entry crosses, the first spyable link stays current. At the
// scroller bottom the last *reachable* entry wins, so a paginated page 1 does
// not light up a heading that still lives on page 5.
//
// Paginated / flipbook: every entry on an earlier page has already been passed.
// Turning onto a page therefore starts at that page's first TOC entry, and the
// heading spy only advances among headings that are actually in the DOM.

const HEADING_TOLERANCE = 1

export function activeTocLink({ entries, spyLine, atBottom, currentPage }) {
  const spyable = (entries || []).filter((entry) => canSpy(entry))
  if (spyable.length === 0) return null

  const earlier = spyable.filter((entry) => strictlyEarlierPage(entry.page, currentPage))
  const onPage = spyable.filter((entry) => onCurrentPage(entry, currentPage))

  if (atBottom) {
    const reached = onPage.length > 0 ? onPage : earlier
    return (reached[reached.length - 1] || spyable[0]).link
  }

  let current = (onPage[0] || earlier[earlier.length - 1] || spyable[0]).link
  onPage.forEach((entry) => {
    if (headingCrossed(entry, spyLine)) current = entry.link
  })
  return current
}

export function parseTocPage(raw) {
  if (raw == null || raw === "") return null
  if (raw === "unpaged") return "unpaged"

  const number = parseInt(raw, 10)
  return Number.isInteger(number) ? number : null
}

function canSpy(entry) {
  if (entry.link?.classList.contains("is-unbound")) return false
  return Boolean(visibleHeading(entry) || entry.page != null)
}

function visibleHeading(entry) {
  const heading = entry.heading
  return heading && heading.offsetParent !== null ? heading : null
}

function headingCrossed(entry, spyLine) {
  const heading = visibleHeading(entry)
  if (!heading) return false
  return heading.getBoundingClientRect().top <= spyLine + HEADING_TOLERANCE
}

function onCurrentPage(entry, currentPage) {
  if (pageEquals(entry.page, currentPage)) return true
  return entry.page == null && Boolean(visibleHeading(entry))
}

function pageEquals(entryPage, currentPage) {
  if (entryPage == null || currentPage == null) return false
  return String(entryPage) === String(currentPage)
}

function strictlyEarlierPage(entryPage, currentPage) {
  if (entryPage == null || currentPage == null) return false
  if (currentPage === "unpaged") return entryPage !== "unpaged"
  if (entryPage === "unpaged") return false

  const entryNumber = Number(entryPage)
  const currentNumber = Number(currentPage)
  if (!Number.isFinite(entryNumber) || !Number.isFinite(currentNumber)) return false
  return entryNumber < currentNumber
}
